import type { TenantContext, TenantMode, TenantMembership } from '~/types/types'
import { useTenantRepository } from '~/repositories/tenantRepository'

const normalizeHost = (value: string): string => value.trim().toLowerCase()
const getHostnameOnly = (host: string): string => host.split(':')[0] || host
const isNotEmpty = (value: string | undefined): value is string => Boolean(value && value.trim().length)

const getCurrentHost = (): string => {
  if (import.meta.server) {
    const headers = useRequestHeaders(['x-forwarded-host', 'host'])
    const forwardedHost = headers['x-forwarded-host']
    const hostHeader = headers.host
    const raw = (typeof forwardedHost === 'string' && forwardedHost) || (typeof hostHeader === 'string' && hostHeader) || useRequestURL().host

    return normalizeHost(raw)
  }

  return normalizeHost(window.location.host)
}

const getCurrentPath = (): string => {
  if (import.meta.server) {
    return useRequestURL().pathname || '/'
  }

  return window.location.pathname || '/'
}

export const useTenant = () => {
  const repository = useTenantRepository()
  const config = useRuntimeConfig()
  const tenant = useState<TenantContext | null>('tenant-context', () => null)
  const resolved = useState<boolean>('tenant-resolved', () => false)
  const tenantError = useState<string | null>('tenant-error', () => null)
  const tenantMode = useState<TenantMode>('tenant-mode', () => 'platform')
  const routeSlug = useState<string | null>('tenant-route-slug', () => null)
  const resolveKey = useState<string | null>('tenant-resolve-key', () => null)
  const membership = useState<TenantMembership | null>('tenant-membership', () => null)
  const membershipInflight = useState<Promise<TenantMembership | null> | null>('tenant-membership-inflight', () => null)
  const { apply } = useTenantTheme()

  const platformHosts = computed(() => {
    const legacyHosts = String(config.public.marketplaceHosts || '')
    const nextHosts = String(config.public.platformHosts || '')
    return [...legacyHosts.split(','), ...nextHosts.split(',')]
      .map(host => normalizeHost(host))
      .filter(isNotEmpty)
  })

  const isMarketplaceHost = computed(() => {
    const currentHost = getCurrentHost()
    const currentHostname = getHostnameOnly(currentHost)

    return platformHosts.value.some(host =>
      host === currentHost || getHostnameOnly(host) === currentHostname
    )
  })

  const detectContext = (overridePath?: string) => {
    const host = getCurrentHost()
    const hostname = getHostnameOnly(host)
    const currentPath = overridePath ?? getCurrentPath()
    const rootDomain = String(config.public.platformRootDomain || 'zerotohero.network').toLowerCase().trim()

    // agency-saas is subdomain-only (see composables/useCommunityLinks.ts
    // for the architectural rationale). No path-based /c/{slug} tenant
    // routing — all tenants live on {slug}.zerotohero.network or a custom
    // whitelisted domain configured per tenant by the platform admin.

    // 1. Root domain exactly → platform host
    if (rootDomain && (hostname === rootDomain || hostname === `www.${rootDomain}`)) {
      tenantMode.value = 'platform'
      routeSlug.value = null
      return
    }

    // 2. Subdomain of the configured root domain → Pro plan tenant
    if (rootDomain && hostname.endsWith(`.${rootDomain}`)) {
      tenantMode.value = 'pro'
      routeSlug.value = hostname.replace(`.${rootDomain}`, '')
      return
    }

    // 3. Subdomain on a non-root host (e.g. *.localhost in dev) → Pro plan
    const parts = hostname.split('.')
    if (parts.length > 1) {
      const maybeSlug = parts[0]
      if (maybeSlug && !platformHosts.value.some(h => getHostnameOnly(h) === hostname)) {
        tenantMode.value = 'pro'
        routeSlug.value = maybeSlug
        return
      }
    }

    // 4. Bare host that matches a configured platform host → platform
    if (isMarketplaceHost.value) {
      tenantMode.value = 'platform'
      routeSlug.value = null
      return
    }

    // 5. Anything else is treated as an Enterprise custom domain.
    tenantMode.value = 'enterprise'
    routeSlug.value = null
  }

  const isTenantContext = computed(() => tenantMode.value !== 'platform')

  const checkMembership = async () => {
    const auth = useAuth()
    if (!auth.user.value || !isTenantContext.value) {
      membership.value = null
      return null
    }

    if (membershipInflight.value) return membershipInflight.value

    const p = (async () => {
      try {
        const data = await repository.me()
        membership.value = data
        return data
      } catch {
        membership.value = null
        return null
      }
    })()
    membershipInflight.value = p
    p.finally(() => { membershipInflight.value = null })
    return p
  }

  const resolveTenant = async (force = false, destinationPath?: string) => {
    detectContext(destinationPath)
    const host = getCurrentHost()
    const currentKey = `${host}|${tenantMode.value}|${routeSlug.value || ''}`

    if (!force && resolved.value && resolveKey.value === currentKey) {
      return tenant.value
    }

    tenantError.value = null
    resolveKey.value = currentKey

    try {
      if (tenantMode.value === 'platform') {
        tenant.value = null
        resolved.value = true
        apply(null)
        return null
      }

      const current = await repository.resolve({
        slug: routeSlug.value,
        host
      })
      tenant.value = current
      resolved.value = true
      apply(current)
      return current
    } catch (error) {
      tenantError.value = error instanceof Error ? error.message : 'Tenant resolve failed'
      tenant.value = null

      // Only mark as resolved (cache the miss) when the community genuinely
      // doesn't exist (404). For transient failures (network error, 5xx, 429)
      // leave resolved = false so the next navigation retries the API call
      // instead of serving a stale null from cache.
      const statusCode = (error as { statusCode?: number })?.statusCode
      if (statusCode === 404) {
        resolved.value = true
      } else {
        resolved.value = false
        resolveKey.value = null
      }

      return null
    }
  }

  return {
    tenant,
    resolved,
    tenantError,
    tenantMode,
    routeSlug,
    membership,
    isMarketplaceHost,
    isTenantContext,
    resolveTenant,
    checkMembership
  }
}
